/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * BulkEditStyles.jsx
 * Styles for bulk edit components
 */

import { makeStyles } from '@material-ui/core';
import mixins from '../../../assets/styles/mixins.scss';

const useBulkEditStyles = makeStyles(() => ({
  bulkEditLink: {
    color: `${mixins.linkDefault} !important`,
    textDecoration: 'underline !important',
    textDecorationStyle: 'solid',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '157.43%',
    cursor: 'pointer'
  },
  bulkEditPaper: {
    display: 'flex',
    maxWidth: '800px !important',
    width: '100% !important',
    padding: '48px !important',
    gap: 16,
    flexShrink: 0,
    maxHeight: '980px !important',
    height: '80vh !important',
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: mixins.whiteFont,
    boxSizing: 'border-box',

    '&:has([data-tab="initial"])': {
      height: '540px !important'
    },
    '&:has([data-tab="uploading"])': {
      '& $bulkEditTitle': {
        display: 'none'
      },
      '& $bulkEditSubtitle': {
        display: 'none'
      }
    }
  },
  modalContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 0
  },
  bulkEditHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '0px !important'
  },
  bulkEditTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0
  },
  closeIcon: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
    padding: '8px',
    cursor: 'pointer',
    color: mixins.mutedBlack,
    '&:hover': {
      backgroundColor: mixins.quaternary,
      borderRadius: '50%'
    }
  },
  tabSection: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px 24px',
    backgroundColor: mixins.whiteFont,
    borderBottom: `1px solid ${mixins.greyColor}`
  },
  tabButton: {
    padding: '8px 24px',
    borderRadius: '1000px',
    border: 'none',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '22px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
    color: mixins.mutedTextColor,
    '&:hover': {
      backgroundColor: mixins.quaternary
    }
  },
  activeTab: {
    backgroundColor: `${mixins.lightThemeBgColor2} !important`,
    color: `${mixins.amethystColor} !important`
  },
  contentSection: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: mixins.whiteFont
  },
  syllabusUploaderContainer: {
    height: '100%',
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    position: 'relative',
    '& > div': {
      padding: '0px !important'
    }
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 10,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center'
  },
  bulkEditSubtitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    color: mixins.color_text_primary
  }
}));

export default useBulkEditStyles;
